// Sentry initialization must be imported before anything else
import './instrument/sentry';

import {
    akitaDevtools,
    persistState,
    PersistStateParams,
} from '@datorama/akita';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { MultiLanguageSupportProvider } from 'core/components/MultiLanguageSupportProvider';
import { GeneralDialogProvider } from 'core/components/shared/GeneralDialog/GeneralDialogProvider';
import { StrictMode, Suspense } from 'react';

import theme from 'styles/material-ui/theme';
import { App } from './App';
import './core/utils/multi-language-support';
import './index.css';

import { createRoot } from 'react-dom/client';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import * as Sentry from '@sentry/react';
import { sentryEnabled } from './instrument/sentry';

const persistStateParams: Partial<PersistStateParams> = {
    include: ['session'],
};

if (import.meta.env.NODE_ENV !== 'production') {
    akitaDevtools();
} else {
    // persistStateParams.preStorageUpdateOperator = () => debounceTime(2000);
}

persistState(persistStateParams);

/**
 * Issue: https://www.gitmemory.com/issue/datorama/akita/634/789712235
 *
 * workaround is to define the resettable flag at store declaration via decorator
 */
// akitaConfig({ resettable: true });

/**
 * We need to wrap app with Suspense because of the translation support,
 * as it loads translation files asynchronously
 */

const container = document.getElementById('root');
const root = createRoot(
    container!,
    sentryEnabled
        ? {
              onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
                  console.warn(
                      'Uncaught error',
                      error,
                      errorInfo.componentStack
                  );
              }),
              onCaughtError: Sentry.reactErrorHandler(),
              onRecoverableError: Sentry.reactErrorHandler(),
          }
        : undefined
);

const app = (
    <Suspense fallback={null}>
        <StyledEngineProvider injectFirst enableCssLayer>
            <ThemeProvider theme={theme}>
                <MultiLanguageSupportProvider>
                    <GeneralDialogProvider>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <CssBaseline />
                            <App />
                        </LocalizationProvider>
                    </GeneralDialogProvider>
                </MultiLanguageSupportProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    </Suspense>
);

root.render(import.meta.env.DEV ? app : <StrictMode>{app}</StrictMode>);
